import { WeightGridKey, WeightGridLine, VisionKey } from '@wizeoo/akm-message-lib';

import { WizConstants } from '../../model/constants';
import { WeightGridVO } from '../../vo/integration/weightgrid.vo';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WidgetLine } from '../../vo/workingdata/widget-line.obj';


/**
 * Classe utilitaire pour les poids des lignes
 */
export class WeightCoreEngine {

  /**
   * pour un code de grille de poids, la map de poids des baselines du pool
   * @param fei le moteur de calcul cible
   * @param weightCode code de la grille
   * @param parentKey code de la ligne à splitter
   */
  private static getWeightMap(fei: FeiVO, weightCode: string, parentKey: string): Map<string, number> {
    let result: Map<string, number> = null;
    if (fei.weightMap && fei.weightMap.has(weightCode)) {
      if (fei.weightMap.get(weightCode).has(parentKey)) {
        result = fei.weightMap.get(weightCode).get(parentKey);
      }
    }
    return result;
  }

  /**
   * alimentation de la grille des poids du fei
   * @param fei le moteur de calcul cible
   * @param weightCode code de la grille
   * @param parentKey code de la ligne à splitter
   * @param values: Map<string, number> la map de clé / poids, avec le total en ligne SPLIT_TOTAL_KEY
   */
  private static setWeight(fei: FeiVO, weightCode: string, parentKey: string, values: Map<string, number>): void {
    if (!fei.weightMap) {
      fei.weightMap = new Map<string, Map<string, Map<string, number>>>();
    }
    if (!fei.weightMap.has(weightCode)) {
      fei.weightMap.set(weightCode, new Map<string, Map<string, number>>());
    }

    fei.weightMap.get(weightCode).set(parentKey, values);
  }

  /**
   * pour un code de grille de poids, retourne le poids de la ligne dans le pool de données
   * @param fei le moteur de calcul cible
   * @param weightCode code de la grille
   * @param parentKey code de la ligne à splitter
   * @param key: la clé unique de la baseline cible
   */
  private static getWeight(fei: FeiVO, weightCode: string, parentKey: string, key: string): number {
    let result: number = 1; // par défaut, poids de 1
    const map: Map<string, number> = this.getWeightMap(fei, weightCode, parentKey);
    if (map && map.has(key)) {
      result = map.get(key);
    }
    return result;
  }

  /**
   * Construction de la map des lignes soeurs Les "vraies" soeurs + les soeurs en dehors du périmètre
   * alimente nextStepChildren sur la motherBaseline
   * @param motherLine la ligne mère
   * @param baselines  Baseline les lignes de travail du fei
   */
  public static feedSistersMap(motherLine: WidgetLine, baselines: WidgetLine[]): void {
    // if (!motherLine.sistersChildren) {
    //   // la ligne mère n'a pas encore la liste de ses enfants
    //   const result: WidgetLine[] = [];
    //   baselines.forEach((sister: WidgetLine) => {
    //     if (sister.bl.pkey === motherLine.key) {
    //       result.push(sister);
    //     }
    //   });
    //   motherLine.sistersChildren = result;
    // }
  }

  /**
   * Alimentation des poids des soeurs d'une ligne de travail (s'ils ne sont pas connus) On récupère la définition de la grille de poids concernée
   * ATTENTION, si elle n'est pas définie, on appliquera un poids standard (1) par défaut sur toutes les lignes Puis, si ce n'est pas déjà fait, on
   * détermine le poids des lignes soeurs
   *
   * @param weightGridKey Code de la grille de poids
   * @param motherline la ligne de travail mère
   * @param fei           Instance de gestion des formules
   * @param sourceMeasure, la mesure qui demande le split. En cas de WG absent, on utilisera son code pour alimenter une "fake" WG
   * @param weightNumber, en cas d'alimentation du split avec un indice. Utilisé comme poids pour toutes les soeurs
   * @return void
   */
  public static feedSisterWeightMatrix(
    weightGridKey: string,
    motherline: WidgetLine,
    fei: FeiVO,
    sourceMeasure: string,
    weightNumber: number = 1
  ): void {
    if (!this.getWeightMap(fei, weightGridKey, motherline.key)) {
      // pas encore alimenté, on fait le job (une fois pour toutes les soeurs)
      // Récupération de la définition la grille de poids
      const weightGrid: WeightGridVO = fei.model.weightGridEngine.getWeightGrid(weightGridKey); // fei.weightGrids[weightGridKey];

      // identification des emplacements des weightKey dans la clé du step
      // Uniquement si la grille de poids est définie
      let keyPos: Map<number, string> = null;
      const chidrenLines: WidgetLine[] = null; // TODO TODO motherline.sistersChildren;
      if (weightGrid != null) {
        keyPos = this.getweightKeyPos(weightGrid, fei);
        // ajout de la map des poids au fei par le dataEngine
        this.setWeight(
          fei,
          weightGridKey,
          motherline.key,
          this.feedSistersWeightMap(weightGrid, chidrenLines, keyPos)
        );
      } else {
        // pas trouvé la WG, alimentation de base (1) ou override si 2ème param
        // console.log('no wg', sourceMeasure, weightNumber);
        this.setWeight(
          fei,
          sourceMeasure,
          motherline.key,
          this.feedSistersIsoMap(chidrenLines, weightNumber)
        );
      }
    }
  }

  /**
   * Détermintation des emplacements des weightKey dans la clé du step
   *
   * @param weightGrid Dfinition de la grille de poids
   * @param fei        Instance de gestion des formules
   * @return Map<Long, String>Map des emplacements des weightkey
   */
  private static getweightKeyPos(weightGrid: WeightGridVO, fei: FeiVO): Map<number, string> {
    const result: Map<number, string> = new Map<number, string>();

    weightGrid.weightGridKeys.forEach((wgk: WeightGridKey) => {
      Object.values(fei.vision.visionKeys).forEach((vk: VisionKey) => {
        if (wgk.code === vk.code) {
          result.set(vk.order, wgk.code);
        }
      });
    });
    return result;
  }

  /** Répartition sur le total */
  public static readonly SPLIT_TOTAL_KEY: string = 'TOTAL';

  /**
   * Construction de la grille de poids par rapport aux soeurs et la définition de la grille de poids Si celle-ci n'est pas définie, on applique le
   * poids standard (1)
   *
   * @param fei le moteur de calcul
   * @param weightGrid   La définition de la grille de poids utilisée
   * @param lines      Les lignes de travail soeurs
   * @param weightKeyPos Les emplacements des weightKey dans la clé du step
   */
  private static feedSistersWeightMap(
    weightGrid: WeightGridVO,
    lines: WidgetLine[],
    weightKeyPos: Map<number, string>
  ): Map<string, number> {
    const result: Map<string, number> = new Map<string, number>(); // dic[line.key = weight]
    // poids total
    result.set(this.SPLIT_TOTAL_KEY, 0); // ajout de l'entrée de cumul, pour ne plus recalculer

    // TODO prendre en compte les steps perimeters

    // pour chaque ligne, calcul du poids
    lines.forEach((sister: WidgetLine) => {
      // Calcul du poids de la soeur
      let newWeight: number = 1;
      if (weightGrid !== null) {
        newWeight = this.getSisterWeight(weightGrid, sister, weightKeyPos);
      }
      // et ajout au cumul de lignes
      const newTot: number = result.get(this.SPLIT_TOTAL_KEY) + newWeight;
      result.set(sister.key, newWeight);
      result.set(this.SPLIT_TOTAL_KEY, newTot);
    });

    // contrôle de cohérence : si TOTAL=0 (rien de défini) => tous les poids à 1
    if (result.get(this.SPLIT_TOTAL_KEY) === 0) {
      let cpt: number = 0;
      Array.from(result.keys()).forEach((mapKey: string) => {
        result.set(mapKey, 1);
        cpt++;
      });
      result.set(this.SPLIT_TOTAL_KEY, cpt - 1); // il fait partie du compte des properties
    }
    return result;
  }

  /**
   * Construction de la grille de poids iso pour toutes les soeurs, avec indice
   * poids standard (1)
   *
   * @param fei le moteur de calcul
   * @param weightNumber Le poids à appliquer
   */
  private static feedSistersIsoMap(lines: WidgetLine[], weightNumber: number): Map<string, number> {
    const result: Map<string, number> = new Map<string, number>(); // dic[line.key = weight]
    // poids total
    result.set(this.SPLIT_TOTAL_KEY, 0); // ajout de l'entrée de cumul, pour ne plus recalculer

    // TODO prendre en compte les steps perimeters

    // pour chaque ligne, calcul du poids
    lines.forEach((sister: WidgetLine) => {
      // et ajout au cumul de lignes
      const newTot: number = result.get(this.SPLIT_TOTAL_KEY) + 1;
      result.set(sister.key, weightNumber);
      result.set(this.SPLIT_TOTAL_KEY, newTot);
    });
    return result;
  }

  /**
   * Calcul du poids d'une soeur On le récupère grâce à la définition de la grille de poids Si la clé de poids n'est pas définie dans la grille, on
   * applique le poids standard (1)
   *
   * @param weightGrid   La définition de la grille de poids à utiliser
   * @param sister       La ligne de travail soeur concernée
   * @param weightKeyPos Les emplacements des weightKey dans la clé du step
   * @return BigDecimal Le poids de la ligne de grille de poids correspondante si trouvée, sinon 1 (poids standard)
   */
  private static getSisterWeight(
    weightGrid: WeightGridVO,
    sister: WidgetLine,
    weightKeyPos: Map<number, string>
  ): number {
    const sisterKey: string[] = sister.key.split(WizConstants.KEY_SEPARATOR);
    const usedKey: string[] = [];
    Array.from(weightKeyPos.keys()).forEach((digit: number) => {
      usedKey.push(sisterKey[digit - 1]);
    });

    // recherche de la ligne de poids correspondante
    const weightGridLineMap: Map<string, WeightGridLine> = new Map(
      weightGrid.weightGridLines.map((wgl: WeightGridLine) => [wgl.key, wgl])
    );
    const sisterWeight: WeightGridLine = weightGridLineMap.get(usedKey.join('|'));

    let result = 1; // sinon 1 (poids standard)
    if (sisterWeight !== null && sisterWeight !== undefined) {
      result = sisterWeight.weight;
    }

    return result;
  }
}
